import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from "@heroicons/react/24/outline";
import { useCreateLesson } from "../../api/mutations";
import { checkLessonSlug } from "../../api/client";
import { useSlugValidation } from "../../hooks/useSlugValidation";
import SlugInputField from "../../componentLibrary/SlugInputField";
import {
  LessonI,
  ComponentI,
  ComponentEnum,
  BottomNavigationBarType,
} from "../../types";
import Container from "../../componentLibrary/Container";
import Input from "../../componentLibrary/Input";
import { useNotification } from "../../hooks/useNotification";
import { useComponentMinimization } from "../../hooks/useComponentMinimization";
import IconSelect from "../ContentManagementPage/components/IconSelect";
import TabGroup from "../../componentLibrary/TabGroup";
import { ImageTypeEnum } from "../../types";
import { useGetTopics } from "../../api/queries";
import Spinner from "../../componentLibrary/Spinner";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../componentLibrary/MultiSelectDropdown";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import FloatingActionButton from "../../componentLibrary/FloatingActionButton";
import Button from "../../componentLibrary/Button";
import {
  SelectedComponentType,
  handleInsertComponent,
  handlePastePreview,
} from "./utils/selectedComponentUtils";
import PageComponentContainer from "./components/PageComponentContainer";
import ComponentList from "./components/ComponentList";
import PasteComponentModal, {
  PasteWhere,
} from "./components/PasteComponentModal";
import PageList from "./components/PageList";
import SubmitAndPreviewButtons from "./components/SubmitAndPreviewButtons";
import { validateLesson } from "./components/SubmitAndPreviewButtons";
import LessonErrorModal from "./components/LessonErrorModal";
import {
  lessonHasUnsupportedComponents,
  getLessonWithUnsupportedRemoved,
} from "./utils/unsupportedComponents";
import UnsupportedComponentsSaveModal from "./components/UnsupportedComponentsSaveModal";

const CreateLessonPage = () => {
  const { notify } = useNotification();
  const [currentPageId, setCurrentPageId] = useState<string>();
  const [selectedComponent, setSelectedComponent] =
    useState<SelectedComponentType>({
      component: null,
      parentComponent: null,
    });

  const [isPasteComponentOpen, setIsPasteComponentOpen] =
    useState<boolean>(false);
  const [pasteComponent, setPasteComponent] = useState<ComponentI | null>(null);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isUnsupportedSaveModalOpen, setIsUnsupportedSaveModalOpen] =
    useState(false);

  const navigate = useNavigate();
  const componentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );

  const handleComponentClick = (clickedComponent: SelectedComponentType) => {
    if (
      clickedComponent?.component?._id === selectedComponent?.component?._id
    ) {
      setSelectedComponent({
        component: null,
        parentComponent: null,
      });
    } else {
      setSelectedComponent(clickedComponent);
    }
  };

  const { data: modules, isLoading, isError } = useGetTopics();
  const [linkedModules, setLinkedModules] = useState<MultiSelectOptionI[]>([]);

  const [lesson, setLesson] = useState<LessonI>({
    title: "",
    icon: { src: "", key: "", name: "", alt: "", type: ImageTypeEnum.ICON },
    pages: [{ _id: `page_${Date.now()}`, title: "", components: [] }],
    bottomNavigationBarType: BottomNavigationBarType.MULTI_PAGE,
  });
  const {
    status: slugStatus,
    validate: validateSlug,
    reset: resetSlugStatus,
  } = useSlugValidation(checkLessonSlug);
  const [warnings, setWarnings] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const mutation = useCreateLesson({
    onSuccess: async () => {
      notify("success", "Lesson created successfully");
      const newPageId = `page_${Date.now()}`;
      setCurrentPageId(newPageId);
      setLesson({
        title: "",
        icon: { src: "", key: "", name: "", alt: "", type: ImageTypeEnum.ICON },
        pages: [{ _id: newPageId, title: "", components: [] }],
      });
      setLinkedModules([]);
      await queryClient.invalidateQueries({ queryKey: ["lessons"] });
    },
    onError: async () => {
      notify("error", "There was an error creating the lesson.");
    },
  });

  useEffect(() => {
    if (lesson.pages.length > 0 && !currentPageId) {
      setCurrentPageId(lesson.pages[0]._id);
    }
  }, [lesson.pages, currentPageId]);

  const handleFocusComponent = (component: ComponentI | null) => {
    if (component && componentRefs.current[component._id]) {
      componentRefs.current[component._id]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const tabData = [
    {
      label: "Pages",
      content: (
        <PageList
          lesson={lesson}
          setLesson={setLesson}
          currentPageId={currentPageId}
          setCurrentPageId={setCurrentPageId}
        />
      ),
    },
    {
      label: "Components",
      content: (
        <ComponentList
          lesson={lesson}
          setLesson={setLesson}
          currentPageId={currentPageId}
          selectedComponent={selectedComponent}
          setSelectedComponent={setSelectedComponent}
          handleFocusComponent={handleFocusComponent}
        />
      ),
    },
  ];

  const handlePasteComponent = (
    component: ComponentI,
    pasteWhere?: PasteWhere,
  ) => {
    handleInsertComponent(
      lesson,
      setLesson,
      currentPageId,
      component,
      selectedComponent,
      pasteWhere,
      setSelectedComponent,
      handleFocusComponent,
      notify,
    );
  };

  const currentPageIndex = lesson.pages.findIndex(
    (page) => page._id === currentPageId,
  );
  const currentPage = lesson.pages[currentPageIndex];

  const {
    minimizedComponents,
    setMinimizedComponents,
    handleMinimizeAll,
    handleExpandAll,
    areAllMinimized,
  } = useComponentMinimization(currentPage);

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [lessonErrors, setLessonErrors] = useState<{ text: string }[]>([]);
  const [totalPageErrors, setTotalPageErrors] = useState<
    {
      pageTitle: string;
      pageErrors: {
        text: string;
        pageId: string;
        componentId: string;
      }[];
    }[]
  >([]);

  const onCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    setLessonErrors([]);
    setTotalPageErrors([]);
  };

  const onLessonErrorCallback = (
    lessonErrors: { text: string }[],
    totalPageErrors: {
      pageTitle: string;
      pageErrors: {
        text: string;
        pageId: string;
        componentId: string;
      }[];
    }[],
  ) => {
    setIsErrorModalOpen(true);
    setLessonErrors(lessonErrors);
    setTotalPageErrors(totalPageErrors);
  };

  const onErrorClick = (pageId, componentId) => {
    setCurrentPageId(pageId);
    if (componentId) {
      const page = lesson.pages.find((p) => p._id === pageId);
      if (page) {
        const component = page.components.find((c) => c._id === componentId);
        if (component) {
          setSelectedComponent({
            component,
            parentComponent: null,
          });
          setTimeout(() => {
            handleFocusComponent(component);
          }, 200);
        }
      }
    }
  };

  if (isLoading) return <Spinner />;
  if (isError || !modules) return <div>Error</div>;

  // Extract input variables from pages up to the current page
  const variablesByPage = lesson.pages.map((page) =>
    page.components
      .flatMap((component) =>
        component.type === ComponentEnum.LOGIC_COMPONENT
          ? (component.content?.componentsList?.map(
              (comp) => comp.content.variable,
            ) ?? [])
          : component.content.variable
            ? [component.content.variable]
            : [],
      )
      .filter(Boolean),
  );

  const inputVariablesUpToCurrentPage = variablesByPage
    .slice(0, currentPageIndex + 1)
    .flat();

  const inputVariablesUpToPreviousPage = variablesByPage
    .slice(0, currentPageIndex)
    .flat();

  const handleBack = () => {
    setIsConfirmationOpen(true);
  };

  const handleSaveAndExit = () => {
    if (!validateLesson(lesson, onLessonErrorCallback)) {
      notify("error", "Please Resolve the errors");
      setIsConfirmationOpen(false);
      return;
    }
    if (lessonHasUnsupportedComponents(lesson)) {
      setIsUnsupportedSaveModalOpen(true);
      return;
    }
    mutation.mutate({
      lesson: lesson,
      modules: linkedModules.map((module) => module.value),
    });
    setIsConfirmationOpen(false);
    navigate("/admin/content-management");
  };

  const handleUnsupportedSaveAsIsAndExit = () => {
    setIsUnsupportedSaveModalOpen(false);
    setIsConfirmationOpen(false);
    mutation.mutate({
      lesson,
      modules: linkedModules.map((module) => module.value),
    });
    navigate("/admin/content-management");
  };

  const handleUnsupportedDeleteAndSaveAndExit = () => {
    const filteredLesson = getLessonWithUnsupportedRemoved(lesson);
    setLesson(filteredLesson);
    setIsUnsupportedSaveModalOpen(false);
    setIsConfirmationOpen(false);
    mutation.mutate({
      lesson: filteredLesson,
      modules: linkedModules.map((module) => module.value),
    });
    navigate("/admin/content-management");
  };

  const handleDiscardAndExit = () => {
    setIsConfirmationOpen(false);
    navigate("/admin/content-management");
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
  };

  return (
    <div className="flex flex-col flex-grow">
      <div className="px-2 mb-1 mt-1">
        <SubmitAndPreviewButtons
          lesson={lesson}
          mutation={mutation}
          selectedComponent={selectedComponent}
          handlePastePreview={() =>
            handlePastePreview(
              setPasteComponent,
              setIsPasteComponentOpen,
              notify,
            )
          }
          linkedModules={linkedModules.map((module) => module.value)}
          currentPageId={currentPageId}
          setCurrentPageId={setCurrentPageId}
          handleBackFunction={handleBack}
          onLessonFilteredForSave={setLesson}
          onLessonErrorCallback={onLessonErrorCallback}
          onMinimizeAll={handleMinimizeAll}
          onExpandAll={handleExpandAll}
          areAllMinimized={areAllMinimized}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 px-0.5 md:px-4">
        <div className="md:col-span-5 lg:col-span-4 xl:col-span-3 w-full flex flex-col min-h-0 max-h-[55vh] md:max-h-[77vh]">
          <Container className="flex flex-col flex-1 min-h-0 w-full px-1 md:px-6 overflow-hidden">
            <TabGroup tabs={tabData} defaultSelectedIndex={1} />
          </Container>
        </div>
        <div className="md:col-span-7 lg:col-span-8 xl:col-span-9 flex flex-col h-full space-y-4">
          <Container className="flex flex-col flex-grow space-y-2 max-h-[55vh] md:max-h-[77vh] overflow-y-auto px-1 md:px-6">
            <h3 className="mb-2">Lesson</h3>
            <Input
              label="Title"
              value={lesson.title}
              onChange={(e) => setLesson({ ...lesson, title: e.target.value })}
              autoFocus
            />
            <SlugInputField
              value={lesson.slug || ""}
              onChange={(slug) => setLesson((prev) => ({ ...prev, slug }))}
              onBlur={validateSlug}
              onResetStatus={resetSlugStatus}
              title={lesson.title}
              urlPrefix="/sections/"
              slugStatus={slugStatus}
            />
            <MultiSelectDropdown
              label="Linked Modules"
              options={modules.map((module) => ({
                value: module._id,
                label: module.title,
              }))}
              value={linkedModules}
              onChange={(value) => setLinkedModules(value)}
            />
            <IconSelect
              icon={lesson.icon}
              setIcon={(icon) => setLesson((prev) => ({ ...prev, icon }))}
            />
            <div className="flex flex-row justify-between items-center">
              <div>Show Bottom Navigation Bar</div>
              <div className="flex flex-row gap-2">
                <Button
                  onClick={() =>
                    setLesson((prev) => ({
                      ...prev,
                      bottomNavigationBarType:
                        BottomNavigationBarType.MULTI_PAGE,
                    }))
                  }
                  variant={
                    lesson.bottomNavigationBarType ===
                    BottomNavigationBarType.MULTI_PAGE
                      ? "primary"
                      : "secondary"
                  }
                >
                  Multi Page
                </Button>
                <Button
                  onClick={() =>
                    setLesson((prev) => ({
                      ...prev,
                      bottomNavigationBarType:
                        BottomNavigationBarType.SINGLE_PAGE,
                    }))
                  }
                  variant={
                    lesson.bottomNavigationBarType ===
                    BottomNavigationBarType.SINGLE_PAGE
                      ? "primary"
                      : "secondary"
                  }
                >
                  Single Page
                </Button>
                <Button
                  onClick={() =>
                    setLesson((prev) => ({
                      ...prev,
                      bottomNavigationBarType: BottomNavigationBarType.NONE,
                    }))
                  }
                  variant={
                    lesson.bottomNavigationBarType ===
                    BottomNavigationBarType.NONE
                      ? "primary"
                      : "secondary"
                  }
                >
                  None
                </Button>
              </div>
            </div>
            {currentPageId && (
              <div className="flex flex-col flex-grow bg-[#F5F5F5] rounded-lg p-2 md:p-4 space-y-2 shadow-md border border-[#E0E0E0]">
                <PageComponentContainer
                  lesson={lesson}
                  setLesson={setLesson}
                  currentPageId={currentPageId}
                  selectedComponent={selectedComponent}
                  handleComponentClick={handleComponentClick}
                  variableUptoCurrentPage={inputVariablesUpToCurrentPage}
                  variableUptoPreviousPage={inputVariablesUpToPreviousPage}
                  setWarnings={setWarnings}
                  componentRefs={componentRefs}
                  minimizedComponents={minimizedComponents}
                  setMinimizedComponents={setMinimizedComponents}
                />
              </div>
            )}
            {warnings?.length > 0 && (
              <div className="mb-4">
                {warnings.map((warning, index) => (
                  <div key={index} className="text-yellow-600 text-sm">
                    ⚠️ {warning}
                  </div>
                ))}
              </div>
            )}
          </Container>
        </div>
      </div>
      <ConfirmationBox
        isOpen={isConfirmationOpen}
        title="Save Changes"
        message="Do you want to save your changes before leaving?"
        confirmText="Save & Exit"
        cancelText="Discard & Exit"
        onConfirm={handleSaveAndExit}
        onCancel={handleDiscardAndExit}
        onCloseBox={handleCancel}
      />
      <UnsupportedComponentsSaveModal
        isOpen={isUnsupportedSaveModalOpen}
        onSaveAsIs={handleUnsupportedSaveAsIsAndExit}
        onDeleteAndSave={handleUnsupportedDeleteAndSaveAndExit}
        onCancel={() => setIsUnsupportedSaveModalOpen(false)}
      />
      <PasteComponentModal
        isModalOpen={isPasteComponentOpen}
        setIsModalOpen={setIsPasteComponentOpen}
        pasteComponent={pasteComponent}
        setPasteComponent={setPasteComponent}
        handlePasteComponent={handlePasteComponent}
        selectedComponent={selectedComponent}
      />
      <LessonErrorModal
        isModalOpen={isErrorModalOpen}
        onCloseModal={onCloseErrorModal}
        lessonErrors={lessonErrors}
        totalPageErrors={totalPageErrors}
        onErrorClick={onErrorClick}
      />
      {selectedComponent?.component && (
        <FloatingActionButton
          variant="primary"
          onClick={() => handleFocusComponent(selectedComponent?.component)}
          className="p-2"
          title="Move to Selected Component"
        >
          <EyeIcon className="w-8 h-8" />
        </FloatingActionButton>
      )}
    </div>
  );
};

export default CreateLessonPage;
